import React from 'react';
import { FiBarChart2 } from 'react-icons/fi';
import { Mafs, Coordinates, Plot, Point, Text, Line } from 'mafs';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

// ============================================
// QUARTILES AND PERCENTILES VISUAL COMPONENTS
// ============================================

// Box Plot Visual - Comprehensive visualization of quartiles
const BoxPlotVisual = () => (
  <div
    style={{
      padding: '1.5rem',
      background: 'white',
      borderRadius: '12px',
      border: '2px solid #e5e7eb',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
        color: '#1f2937',
        fontSize: '1.4rem',
        fontWeight: '600',
      }}
    >
      Box Plot (Box-and-Whisker Plot)
    </h4>
    <Mafs width={650} height={400} viewBox={{ x: [-2, 22], y: [-8, 8] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: false, labels: false }}
        yAxis={{ lines: false, labels: false }}
      />

      {/* Dataset label at top */}
      <Text x={10} y={7} color="#1f2937" size={16} weight="bold">
        Dataset: 2, 4, 6, 8, 10, 12, 14, 16, 18, 20
      </Text>

      {/* Number line */}
      <Plot.OfX y={() => 0} color="#374151" strokeWidth={2} domain={[0, 21]} />

      {/* Tick marks */}
      {[0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20].map((num) => (
        <React.Fragment key={num}>
          <Plot.OfY
            x={() => num}
            color="#374151"
            strokeWidth={2}
            domain={[-0.3, 0.3]}
          />
          <Text x={num} y={-1} color="#374151" size={12}>
            {num}
          </Text>
        </React.Fragment>
      ))}

      {/* Left whisker (Min to Q1) */}
      <Plot.OfX y={() => 2} color="#6366f1" strokeWidth={3} domain={[2, 6]} />
      <Plot.OfY
        x={() => 2}
        color="#6366f1"
        strokeWidth={3}
        domain={[1.5, 2.5]}
      />

      {/* Box (Q1 to Q3) */}
      <Plot.OfX y={() => 3} color="#6366f1" strokeWidth={3} domain={[6, 16]} />
      <Plot.OfX y={() => 1} color="#6366f1" strokeWidth={3} domain={[6, 16]} />
      <Plot.OfY x={() => 6} color="#6366f1" strokeWidth={3} domain={[1, 3]} />
      <Plot.OfY x={() => 16} color="#6366f1" strokeWidth={3} domain={[1, 3]} />

      {/* Median line inside box */}
      <Plot.OfY x={() => 11} color="#dc2626" strokeWidth={4} domain={[1, 3]} />

      {/* Right whisker (Q3 to Max) */}
      <Plot.OfX y={() => 2} color="#6366f1" strokeWidth={3} domain={[16, 20]} />
      <Plot.OfY
        x={() => 20}
        color="#6366f1"
        strokeWidth={3}
        domain={[1.5, 2.5]}
      />

      {/* Labels */}
      <Text x={2} y={4.2} color="#059669" size={14} weight="bold">
        Min = 2
      </Text>
      <Text x={6} y={4.2} color="#2563eb" size={14} weight="bold">
        Q1 = 6
      </Text>
      <Text x={11} y={4.5} color="#dc2626" size={14} weight="bold">
        Median = 11
      </Text>
      <Text x={16} y={4.2} color="#2563eb" size={14} weight="bold">
        Q3 = 16
      </Text>
      <Text x={20} y={4.2} color="#059669" size={14} weight="bold">
        Max = 20
      </Text>

      {/* IQR annotation */}
      <Plot.OfX
        y={() => -3}
        color="#f59e0b"
        strokeWidth={2}
        strokeDasharray="5,5"
        domain={[6, 16]}
      />
      <Text x={11} y={-4.5} color="#f59e0b" size={14} weight="bold">
        IQR = Q3 - Q1 = 16 - 6 = 10
      </Text>

      {/* Percentile annotations */}
      <Text x={2} y={-6.5} color="#6b7280" size={12}>
        0th %ile
      </Text>
      <Text x={6} y={-6.5} color="#6b7280" size={12}>
        25th %ile
      </Text>
      <Text x={11} y={-6.5} color="#6b7280" size={12}>
        50th %ile
      </Text>
      <Text x={16} y={-6.5} color="#6b7280" size={12}>
        75th %ile
      </Text>
      <Text x={20} y={-6.5} color="#6b7280" size={12}>
        100th %ile
      </Text>
    </Mafs>
  </div>
);

// Quartile Calculation Visual
const QuartileCalculationVisual = () => (
  <div
    style={{
      padding: '1.5rem',
      background: 'white',
      borderRadius: '12px',
      border: '2px solid #e5e7eb',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
        color: '#1f2937',
        fontSize: '1.4rem',
        fontWeight: '600',
      }}
    >
      How to Find Quartiles
    </h4>
    <Mafs width={650} height={450} viewBox={{ x: [-1, 16], y: [-6, 10] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: false, labels: false }}
        yAxis={{ lines: false, labels: false }}
      />

      {/* Step 1: Order data */}
      <Text x={7.5} y={9} color="#1f2937" size={16} weight="bold">
        Step 1: Order the data
      </Text>
      <Text x={7.5} y={7.5} color="#6366f1" size={14}>
        3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23
      </Text>

      {/* Visual representation of ordered data */}
      {[3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23].map((value, index) => (
        <React.Fragment key={index}>
          <Point
            x={index * 1.3 + 1}
            y={5.5}
            color={
              index === 5
                ? '#dc2626' // Median
                : index === 2 || index === 8
                  ? '#2563eb' // Q1, Q3
                  : '#6b7280'
            }
          />
          <Text
            x={index * 1.3 + 1}
            y={4.8}
            color={
              index === 5
                ? '#dc2626'
                : index === 2 || index === 8
                  ? '#2563eb'
                  : '#6b7280'
            }
            size={11}
          >
            {value}
          </Text>
        </React.Fragment>
      ))}

      {/* Step 2: Find Median */}
      <Text x={7.5} y={3.5} color="#1f2937" size={14} weight="bold">
        Step 2: Find the Median (Q2)
      </Text>
      <Text x={7.5} y={2.5} color="#dc2626" size={13}>
        Middle value = 13 (6th position of 11 values)
      </Text>

      {/* Step 3: Find Q1 */}
      <Text x={4} y={1} color="#1f2937" size={14} weight="bold">
        Step 3: Q1
      </Text>
      <Text x={4} y={0} color="#2563eb" size={12}>
        Median of lower half: 3, 5, 7, 9, 11
      </Text>
      <Text x={4} y={-1} color="#2563eb" size={13} weight="bold">
        Q1 = 7
      </Text>

      {/* Step 4: Find Q3 */}
      <Text x={11} y={1} color="#1f2937" size={14} weight="bold">
        Step 4: Q3
      </Text>
      <Text x={11} y={0} color="#2563eb" size={12}>
        Median of upper half: 15, 17, 19, 21, 23
      </Text>
      <Text x={11} y={-1} color="#2563eb" size={13} weight="bold">
        Q3 = 19
      </Text>

      {/* Summary */}
      <Text x={7.5} y={-3} color="#059669" size={14} weight="bold">
        Five-Number Summary: Min=3, Q1=7, Q2=13, Q3=19, Max=23
      </Text>
      <Text x={7.5} y={-4.5} color="#f59e0b" size={14} weight="bold">
        IQR = Q3 - Q1 = 19 - 7 = 12
      </Text>
    </Mafs>
  </div>
);

// Percentile Visual - Based on standard quartile diagram
const PercentileVisual = () => (
  <div
    style={{
      padding: '1.5rem',
      background: 'white',
      borderRadius: '12px',
      border: '2px solid #e5e7eb',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
        color: '#1f2937',
        fontSize: '1.4rem',
        fontWeight: '600',
      }}
    >
      Understanding Quartiles & Percentiles
    </h4>
    <Mafs width={650} height={480} viewBox={{ x: [-2, 14], y: [-10, 8] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: false, labels: false }}
        yAxis={{ lines: false, labels: false }}
      />

      {/* Bell curve outline */}
      <Plot.OfX
        y={(x) => 5.5 * Math.exp(-0.5 * Math.pow((x - 6) / 2, 2)) + 0.5}
        color="#374151"
        strokeWidth={2}
        domain={[0, 12]}
      />

      {/* Horizontal baseline */}
      <Plot.OfX
        y={() => 0.5}
        color="#374151"
        strokeWidth={2}
        domain={[0, 12]}
      />

      {/* Vertical lines for quartiles */}
      <Plot.OfY
        x={() => 3}
        color="#374151"
        strokeWidth={2}
        domain={[0.5, 3.2]}
      />
      <Plot.OfY x={() => 6} color="#dc2626" strokeWidth={3} domain={[0.5, 6]} />
      <Plot.OfY
        x={() => 9}
        color="#374151"
        strokeWidth={2}
        domain={[0.5, 3.2]}
      />

      {/* 25% labels in each section */}
      <Text x={1.5} y={1.8} color="#374151" size={14}>
        25%
      </Text>
      <Text x={4.5} y={3.5} color="#374151" size={14}>
        25%
      </Text>
      <Text x={7.5} y={3.5} color="#374151" size={14}>
        25%
      </Text>
      <Text x={10.5} y={1.8} color="#374151" size={14}>
        25%
      </Text>

      {/* Median label with arrow */}
      <Text x={7.5} y={6.5} color="#1f2937" size={14} weight="bold">
        Median
      </Text>
      <Plot.OfX
        y={() => 6.2}
        color="#1f2937"
        strokeWidth={1.5}
        domain={[6.2, 7.2]}
      />

      {/* Arrows pointing up from baseline */}
      <Plot.OfY
        x={() => 3}
        color="#374151"
        strokeWidth={2}
        domain={[-1.5, 0]}
      />
      <Plot.OfY
        x={() => 6}
        color="#374151"
        strokeWidth={2}
        domain={[-1.5, 0]}
      />
      <Plot.OfY
        x={() => 9}
        color="#374151"
        strokeWidth={2}
        domain={[-1.5, 0]}
      />
      <Plot.OfY
        x={() => 12}
        color="#374151"
        strokeWidth={2}
        domain={[-1.5, 0]}
      />

      {/* Percentile labels */}
      <Text x={3} y={-2.3} color="#374151" size={12}>
        25th
      </Text>
      <Text x={3} y={-3.2} color="#374151" size={11}>
        percentile
      </Text>
      <Text x={6} y={-2.3} color="#374151" size={12}>
        50th
      </Text>
      <Text x={6} y={-3.2} color="#374151" size={11}>
        percentile
      </Text>
      <Text x={9} y={-2.3} color="#374151" size={12}>
        75th
      </Text>
      <Text x={9} y={-3.2} color="#374151" size={11}>
        percentile
      </Text>
      <Text x={12} y={-2.3} color="#374151" size={12}>
        100th
      </Text>
      <Text x={12} y={-3.2} color="#374151" size={11}>
        percentile
      </Text>

      {/* Quartile labels */}
      <Text x={3} y={-4.8} color="#1f2937" size={13} weight="bold">
        First Quartile
      </Text>
      <Text x={3} y={-5.8} color="#1f2937" size={12}>
        (Q₁)
      </Text>
      <Text x={9} y={-4.8} color="#1f2937" size={13} weight="bold">
        Third Quartile
      </Text>
      <Text x={9} y={-5.8} color="#1f2937" size={12}>
        (Q₃)
      </Text>

      {/* IQR bracket and label */}
      <Plot.OfX
        y={() => -7.5}
        color="#6366f1"
        strokeWidth={2}
        domain={[3, 9]}
      />
      <Plot.OfY
        x={() => 3}
        color="#6366f1"
        strokeWidth={2}
        domain={[-7.5, -7]}
      />
      <Plot.OfY
        x={() => 9}
        color="#6366f1"
        strokeWidth={2}
        domain={[-7.5, -7]}
      />
      <Text x={6} y={-8.8} color="#6366f1" size={14} weight="bold">
        Interquartile Range = Q₃ - Q₁
      </Text>
    </Mafs>
  </div>
);

// IQR and Outlier Detection Visual
const IQROutlierVisual = () => (
  <div
    style={{
      padding: '1.5rem',
      background: 'white',
      borderRadius: '12px',
      border: '2px solid #e5e7eb',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
        color: '#1f2937',
        fontSize: '1.4rem',
        fontWeight: '600',
      }}
    >
      Using IQR to Detect Outliers
    </h4>
    <Mafs width={700} height={380} viewBox={{ x: [-8, 32], y: [-6, 6] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: false, labels: false }}
        yAxis={{ lines: false, labels: false }}
      />

      {/* Number line */}
      <Plot.OfX y={() => 0} color="#374151" strokeWidth={2} domain={[-6, 30]} />

      {/* Tick marks */}
      {[-4, 0, 4, 8, 12, 16, 20, 24, 28].map((num) => (
        <React.Fragment key={num}>
          <Plot.OfY
            x={() => num}
            color="#374151"
            strokeWidth={1}
            domain={[-0.3, 0.3]}
          />
          <Text x={num} y={-1.2} color="#374151" size={10}>
            {num}
          </Text>
        </React.Fragment>
      ))}

      {/* Q1, Q3, IQR header */}
      <Text x={12} y={5} color="#1f2937" size={14} weight="bold">
        Q1 = 8, Q3 = 16, IQR = 8
      </Text>

      {/* Lower fence */}
      <Plot.OfY
        x={() => -4}
        color="#dc2626"
        strokeWidth={3}
        strokeDasharray="5,5"
        domain={[-0.8, 0.8]}
      />
      <Text x={-4} y={2.5} color="#dc2626" size={11} weight="bold">
        Lower Fence
      </Text>
      <Text x={-4} y={1.5} color="#dc2626" size={10}>
        Q1 - 1.5×IQR
      </Text>
      <Text x={-4} y={-2.5} color="#dc2626" size={11} weight="bold">
        = 8 - 12 = -4
      </Text>

      {/* Box representation */}
      <Plot.OfX y={() => 1} color="#6366f1" strokeWidth={3} domain={[8, 16]} />
      <Plot.OfX y={() => -1} color="#6366f1" strokeWidth={3} domain={[8, 16]} />
      <Plot.OfY x={() => 8} color="#6366f1" strokeWidth={3} domain={[-1, 1]} />
      <Plot.OfY x={() => 16} color="#6366f1" strokeWidth={3} domain={[-1, 1]} />
      <Plot.OfY x={() => 12} color="#059669" strokeWidth={4} domain={[-1, 1]} />

      {/* Box labels */}
      <Text x={8} y={2} color="#6366f1" size={10}>
        Q1
      </Text>
      <Text x={12} y={2} color="#059669" size={10}>
        Median
      </Text>
      <Text x={16} y={2} color="#6366f1" size={10}>
        Q3
      </Text>

      {/* Upper fence */}
      <Plot.OfY
        x={() => 28}
        color="#dc2626"
        strokeWidth={3}
        strokeDasharray="5,5"
        domain={[-0.8, 0.8]}
      />
      <Text x={28} y={2.5} color="#dc2626" size={11} weight="bold">
        Upper Fence
      </Text>
      <Text x={28} y={1.5} color="#dc2626" size={10}>
        Q3 + 1.5×IQR
      </Text>
      <Text x={28} y={-2.5} color="#dc2626" size={11} weight="bold">
        = 16 + 12 = 28
      </Text>

      {/* Outlier zone shading indicator */}
      <Text x={-6} y={3.5} color="#f59e0b" size={10}>
        ← Outliers
      </Text>
      <Text x={30} y={3.5} color="#f59e0b" size={10}>
        Outliers →
      </Text>

      {/* Key insight */}
      <Text x={12} y={-4.5} color="#f59e0b" size={13} weight="bold">
        Values beyond fences are OUTLIERS
      </Text>
    </Mafs>
  </div>
);

// Comparing Distributions with Box Plots
const ComparingBoxPlotsVisual = () => (
  <div
    style={{
      padding: '1.5rem',
      background: 'white',
      borderRadius: '12px',
      border: '2px solid #e5e7eb',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
        color: '#1f2937',
        fontSize: '1.4rem',
        fontWeight: '600',
      }}
    >
      Comparing Distributions Using Box Plots
    </h4>
    <Mafs width={650} height={400} viewBox={{ x: [-2, 22], y: [-3, 10] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: false, labels: false }}
        yAxis={{ lines: false, labels: false }}
      />

      {/* Number line */}
      <Plot.OfX y={() => 0} color="#374151" strokeWidth={2} domain={[0, 20]} />
      {[0, 5, 10, 15, 20].map((num) => (
        <React.Fragment key={num}>
          <Text x={num} y={-1} color="#374151" size={12}>
            {num}
          </Text>
        </React.Fragment>
      ))}

      {/* Class A - Box Plot (higher, narrower) */}
      <Text x={-1} y={7} color="#2563eb" size={14} weight="bold">
        Class A
      </Text>
      <Plot.OfX y={() => 7} color="#2563eb" strokeWidth={2} domain={[8, 10]} />
      <Plot.OfX y={() => 8} color="#2563eb" strokeWidth={2} domain={[10, 16]} />
      <Plot.OfX y={() => 6} color="#2563eb" strokeWidth={2} domain={[10, 16]} />
      <Plot.OfY x={() => 10} color="#2563eb" strokeWidth={2} domain={[6, 8]} />
      <Plot.OfY x={() => 16} color="#2563eb" strokeWidth={2} domain={[6, 8]} />
      <Plot.OfY x={() => 13} color="#2563eb" strokeWidth={3} domain={[6, 8]} />
      <Plot.OfX y={() => 7} color="#2563eb" strokeWidth={2} domain={[16, 18]} />
      <Plot.OfY
        x={() => 8}
        color="#2563eb"
        strokeWidth={2}
        domain={[6.5, 7.5]}
      />
      <Plot.OfY
        x={() => 18}
        color="#2563eb"
        strokeWidth={2}
        domain={[6.5, 7.5]}
      />

      {/* Class B - Box Plot (lower, wider) */}
      <Text x={-1} y={3} color="#dc2626" size={14} weight="bold">
        Class B
      </Text>
      <Plot.OfX y={() => 3} color="#dc2626" strokeWidth={2} domain={[2, 6]} />
      <Plot.OfX y={() => 4} color="#dc2626" strokeWidth={2} domain={[6, 14]} />
      <Plot.OfX y={() => 2} color="#dc2626" strokeWidth={2} domain={[6, 14]} />
      <Plot.OfY x={() => 6} color="#dc2626" strokeWidth={2} domain={[2, 4]} />
      <Plot.OfY x={() => 14} color="#dc2626" strokeWidth={2} domain={[2, 4]} />
      <Plot.OfY x={() => 9} color="#dc2626" strokeWidth={3} domain={[2, 4]} />
      <Plot.OfX y={() => 3} color="#dc2626" strokeWidth={2} domain={[14, 19]} />
      <Plot.OfY
        x={() => 2}
        color="#dc2626"
        strokeWidth={2}
        domain={[2.5, 3.5]}
      />
      <Plot.OfY
        x={() => 19}
        color="#dc2626"
        strokeWidth={2}
        domain={[2.5, 3.5]}
      />

      {/* Analysis */}
      <Text x={10} y={9.5} color="#1f2937" size={13} weight="bold">
        Class A: Higher median (13), smaller spread (IQR=6)
      </Text>
      <Text x={10} y={-2.5} color="#1f2937" size={13} weight="bold">
        Class B: Lower median (9), larger spread (IQR=8)
      </Text>
    </Mafs>
  </div>
);

// Visual Components
const MeasuresComparisonVisual = () => (
  <div
    style={{
      padding: '1.5rem',
      background: 'white',
      borderRadius: '12px',
      border: '2px solid #e5e7eb',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
        color: '#1f2937',
        fontSize: '1.5rem',
        fontWeight: '600',
      }}
    >
      Measures of Central Tendency
    </h4>
    <Mafs width={600} height={350} viewBox={{ x: [-1, 17], y: [-6, 6] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: false, labels: false }}
        yAxis={{ lines: false, labels: false }}
      />

      {/* Dataset label at top */}
      <Text x={8} y={5} color="#1f2937" size={20} weight="bold">
        Data Set: 2, 4, 4, 6, 9, 12, 15
      </Text>

      {/* Number line */}
      <Plot.OfX y={() => 0} color="#1f2937" strokeWidth={2} domain={[0, 16]} />

      {/* Tick marks and labels */}
      {[0, 2, 4, 6, 8, 10, 12, 14, 16].map((num) => (
        <React.Fragment key={num}>
          <Plot.OfX
            y={() => 0.2}
            color="#1f2937"
            strokeWidth={2}
            domain={[num, num]}
          />
          <Text x={num} y={-0.7} color="#1f2937" size={16} weight="bold">
            {num}
          </Text>
        </React.Fragment>
      ))}

      {/* Data points - larger and more visible */}
      {[2, 4, 4, 6, 9, 12, 15].map((value, index) => (
        <Point key={index} x={value} y={0} color="#dc2626" />
      ))}

      {/* Mean indicator with vertical line */}
      <Plot.OfY
        x={() => 7.43}
        color="#dc2626"
        strokeWidth={3}
        strokeDasharray="6,4"
        domain={[0, 2.5]}
      />
      <Text x={7.43} y={3} color="#dc2626" size={18} weight="bold">
        Mean: 7.43
      </Text>

      {/* Median indicator with vertical line */}
      <Plot.OfY
        x={() => 6}
        color="#059669"
        strokeWidth={3}
        strokeDasharray="6,4"
        domain={[-2.5, 0]}
      />
      <Text x={6} y={-3} color="#059669" size={18} weight="bold">
        Median: 6
      </Text>

      {/* Mode indicator with vertical line */}
      <Plot.OfY
        x={() => 4}
        color="#1d4ed8"
        strokeWidth={3}
        strokeDasharray="6,4"
        domain={[-4.5, 0]}
      />
      <Text x={4} y={-5} color="#1d4ed8" size={18} weight="bold">
        Mode: 4
      </Text>

      {/* Explanations at bottom */}
      <Text x={2.5} y={-5.5} color="#6b7280" size={14}>
        Mean: Sum ÷ count
      </Text>
      <Text x={7.5} y={-5.5} color="#6b7280" size={14}>
        Median: Middle value
      </Text>
      <Text x={13} y={-5.5} color="#6b7280" size={14}>
        Mode: Most frequent
      </Text>
    </Mafs>
  </div>
);

const MedianFindingVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Finding the Median
    </h4>
    <Mafs width={400} height={300} viewBox={{ x: [0, 10], y: [-3, 8] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: false, labels: false }}
        yAxis={{ lines: false, labels: false }}
      />

      {/* Odd number example */}
      <Text x={5} y={7} color="#1f2937" size={16} weight="bold">
        Odd number of values: 3, 7, 12, 18, 21
      </Text>

      {/* Odd data visualization */}
      {[3, 7, 12, 18, 21].map((value, index) => (
        <React.Fragment key={index}>
          <Point
            x={index * 1.5 + 2}
            y={5.5}
            color={index === 2 ? '#10b981' : '#6b7280'}
          />
          <Text
            x={index * 1.5 + 2}
            y={5}
            color={index === 2 ? '#10b981' : '#6b7280'}
            size={12}
          >
            {value}
          </Text>
        </React.Fragment>
      ))}

      <Text x={5} y={4} color="#10b981" size={12}>
        Median = 12 (middle value)
      </Text>

      {/* Even number example */}
      <Text x={5} y={2.5} color="#1f2937" size={14}>
        Even number of values: 4, 8, 15, 23, 28, 35
      </Text>

      {/* Even data visualization */}
      {[4, 8, 15, 23, 28, 35].map((value, index) => (
        <React.Fragment key={index}>
          <Point
            x={index * 1.2 + 1.5}
            y={1}
            color={index === 2 || index === 3 ? '#ef4444' : '#6b7280'}
          />
          <Text
            x={index * 1.2 + 1.5}
            y={0.5}
            color={index === 2 || index === 3 ? '#ef4444' : '#6b7280'}
            size={12}
          >
            {value}
          </Text>
        </React.Fragment>
      ))}

      <Text x={5} y={-0.5} color="#ef4444" size={12}>
        Median = (15 + 23) ÷ 2 = 19
      </Text>
      <Text x={5} y={-1.5} color="#6b7280" size={11}>
        When even count, average the two middle values
      </Text>
    </Mafs>
  </div>
);

const DistributionTypesVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Distribution Types
    </h4>
    <Mafs width={500} height={400} viewBox={{ x: [-1, 10], y: [-1, 12] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: false, labels: false }}
        yAxis={{ lines: false, labels: false }}
      />

      {/* Normal Distribution */}
      <Text x={1} y={10.5} color="#1f2937" size={14}>
        Normal Distribution (Symmetric)
      </Text>
      <Plot.OfX
        y={(x) => 3 * Math.exp(-0.5 * Math.pow((x - 4.5) / 0.8, 2)) + 8.5}
        color="#2563eb"
        strokeWidth={2}
        domain={[2, 7]}
      />
      <Plot.OfX
        y={() => 8.5}
        color="#ef4444"
        strokeWidth={2}
        strokeDasharray="5,5"
        domain={[4.5, 4.5]}
      />
      <Text x={4.5} y={8} color="#2563eb" size={12}>
        Mean = Median = Mode
      </Text>

      {/* Right-Skewed Distribution */}
      <Text x={1} y={7} color="#1f2937" size={14}>
        Right-Skewed Distribution
      </Text>
      <Plot.OfX
        y={(x) => {
          if (x < 1 || x > 7) return 5;
          const shifted = (x - 1) / 2;
          return 2 * Math.exp(-shifted) * (shifted > 0 ? 1 : 0) + 5;
        }}
        color="#10b981"
        strokeWidth={2}
        domain={[1, 7]}
      />
      <Plot.OfX
        y={() => 5}
        color="#ef4444"
        strokeWidth={2}
        strokeDasharray="3,3"
        domain={[2, 2]}
      />
      <Text x={2} y={4.5} color="#10b981" size={12}>
        Mode &lt; Median &lt; Mean
      </Text>

      {/* Left-Skewed Distribution */}
      <Text x={1} y={3.5} color="#1f2937" size={14}>
        Left-Skewed Distribution
      </Text>
      <Plot.OfX
        y={(x) => {
          if (x < 1 || x > 7) return 1.5;
          const shifted = (7 - x) / 2;
          return 2 * Math.exp(-shifted) * (shifted > 0 ? 1 : 0) + 1.5;
        }}
        color="#f59e0b"
        strokeWidth={2}
        domain={[1, 7]}
      />
      <Plot.OfX
        y={() => 1.5}
        color="#ef4444"
        strokeWidth={2}
        strokeDasharray="3,3"
        domain={[6, 6]}
      />
      <Text x={5.5} y={1} color="#f59e0b" size={12}>
        Mean &lt; Median &lt; Mode
      </Text>
    </Mafs>
  </div>
);

const OutliersEffectVisual = () => (
  <div
    style={{
      padding: '1.5rem',
      background: 'white',
      borderRadius: '12px',
      border: '2px solid #e5e7eb',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
        color: '#1f2937',
        fontSize: '1.5rem',
        fontWeight: '600',
      }}
    >
      Effect of Outliers
    </h4>
    <Mafs width={550} height={400} viewBox={{ x: [-1, 12], y: [-6, 7] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: false, labels: false }}
        yAxis={{ lines: false, labels: false }}
      />

      {/* Dataset label at top */}
      <Text x={5.5} y={6} color="#1f2937" size={20} weight="bold">
        Dataset: 2, 3, 4, 5, 6, 50 (outlier)
      </Text>

      {/* Number line */}
      <Plot.OfX y={() => 3} color="#1f2937" strokeWidth={2} domain={[0, 10]} />

      {/* Tick marks */}
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
        <React.Fragment key={num}>
          <Plot.OfX
            y={() => 3.2}
            color="#1f2937"
            strokeWidth={2}
            domain={[num, num]}
          />
          <Text x={num} y={2.3} color="#1f2937" size={14} weight="bold">
            {num}
          </Text>
        </React.Fragment>
      ))}

      <Text x={10.5} y={2.3} color="#1f2937" size={14} weight="bold">
        ... 50
      </Text>

      {/* Regular data points - green */}
      {[2, 3, 4, 5, 6].map((value, index) => (
        <Point key={index} x={value} y={3} color="#059669" />
      ))}

      {/* Outlier point - red and larger */}
      <Point x={10} y={3} color="#dc2626" />
      <Text x={10} y={4.2} color="#dc2626" size={18} weight="bold">
        Outlier
      </Text>

      {/* Without outlier stats - on one line */}
      <Text x={5.5} y={0.5} color="#059669" size={17} weight="bold">
        Without outlier: Mean = 4, Median = 4, Mode = none
      </Text>

      {/* With outlier stats - on another line */}
      <Text x={5.5} y={-0.8} color="#dc2626" size={17} weight="bold">
        With outlier: Mean = 11.7, Median = 4.5, Mode = none
      </Text>

      {/* Key insight at bottom with emphasis */}
      <Text x={5.5} y={-3} color="#1f2937" size={18} weight="bold">
        Outliers affect the mean more than median!
      </Text>

      {/* Additional explanation */}
      <Text x={5.5} y={-4.5} color="#6b7280" size={14}>
        Mean changed by 7.7, but median only changed by 0.5
      </Text>
    </Mafs>
  </div>
);

// Transformation Effects Visual - Shows how adding/multiplying affects statistics
const TransformationEffectsVisual = () => (
  <div
    style={{
      padding: '1.5rem',
      background: 'white',
      borderRadius: '12px',
      border: '2px solid #e5e7eb',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
        color: '#1f2937',
        fontSize: '1.4rem',
        fontWeight: '600',
      }}
    >
      Effects of Transformations on Statistics
    </h4>
    <div
      style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}
    >
      {/* Adding a constant */}
      <div
        style={{
          background: '#f0fdf4',
          padding: '1.25rem',
          borderRadius: '10px',
          border: '2px solid #22c55e',
        }}
      >
        <h5
          style={{
            color: '#16a34a',
            marginBottom: '1rem',
            fontSize: '1.1rem',
            fontWeight: '600',
          }}
        >
          Adding a Constant (+ k)
        </h5>
        <p
          style={{
            fontSize: '0.9rem',
            color: '#374151',
            marginBottom: '0.5rem',
          }}
        >
          Original: {'{2, 4, 6, 8, 10}'}
        </p>
        <p
          style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '1rem' }}
        >
          Add 5: {'{7, 9, 11, 13, 15}'}
        </p>
        <div style={{ fontSize: '0.85rem', lineHeight: '1.8' }}>
          <p>
            <strong style={{ color: '#16a34a' }}>✓ Mean:</strong> 6 → 11 (adds
            k)
          </p>
          <p>
            <strong style={{ color: '#16a34a' }}>✓ Median:</strong> 6 → 11 (adds
            k)
          </p>
          <p>
            <strong style={{ color: '#16a34a' }}>✓ Mode:</strong> adds k
          </p>
          <p>
            <strong style={{ color: '#dc2626' }}>✗ Range:</strong> 8 → 8
            (unchanged)
          </p>
          <p>
            <strong style={{ color: '#dc2626' }}>✗ SD:</strong> unchanged
          </p>
          <p>
            <strong style={{ color: '#dc2626' }}>✗ Variance:</strong> unchanged
          </p>
        </div>
      </div>

      {/* Multiplying by a constant */}
      <div
        style={{
          background: '#eff6ff',
          padding: '1.25rem',
          borderRadius: '10px',
          border: '2px solid #3b82f6',
        }}
      >
        <h5
          style={{
            color: '#2563eb',
            marginBottom: '1rem',
            fontSize: '1.1rem',
            fontWeight: '600',
          }}
        >
          Multiplying by a Constant (× k)
        </h5>
        <p
          style={{
            fontSize: '0.9rem',
            color: '#374151',
            marginBottom: '0.5rem',
          }}
        >
          Original: {'{2, 4, 6, 8, 10}'}
        </p>
        <p
          style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '1rem' }}
        >
          Multiply by 3: {'{6, 12, 18, 24, 30}'}
        </p>
        <div style={{ fontSize: '0.85rem', lineHeight: '1.8' }}>
          <p>
            <strong style={{ color: '#2563eb' }}>✓ Mean:</strong> 6 → 18 (× k)
          </p>
          <p>
            <strong style={{ color: '#2563eb' }}>✓ Median:</strong> 6 → 18 (× k)
          </p>
          <p>
            <strong style={{ color: '#2563eb' }}>✓ Mode:</strong> × k
          </p>
          <p>
            <strong style={{ color: '#2563eb' }}>✓ Range:</strong> 8 → 24 (× k)
          </p>
          <p>
            <strong style={{ color: '#2563eb' }}>✓ SD:</strong> × |k|
          </p>
          <p>
            <strong style={{ color: '#2563eb' }}>✓ Variance:</strong> × k²
          </p>
        </div>
      </div>
    </div>

    {/* Summary formulas */}
    <div
      style={{
        marginTop: '1.5rem',
        padding: '1rem',
        background: '#faf5ff',
        borderRadius: '8px',
        border: '1px solid #a855f7',
      }}
    >
      <p
        style={{
          fontWeight: '600',
          color: '#7c3aed',
          marginBottom: '0.75rem',
          fontSize: '1rem',
        }}
      >
        Combined Transformation: Y = kX + c
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0.5rem',
          fontSize: '0.9rem',
        }}
      >
        <p>• New Mean = k × (Old Mean) + c</p>
        <p>• New Median = k × (Old Median) + c</p>
        <p>• New SD = |k| × (Old SD)</p>
        <p>• New Variance = k² × (Old Variance)</p>
        <p>• New Range = |k| × (Old Range)</p>
        <p>• IQR multiplied by |k|</p>
      </div>
    </div>
  </div>
);

// Visual showing numerical example of transformation
const TransformationExampleVisual = () => (
  <div
    style={{
      padding: '1.5rem',
      background: 'white',
      borderRadius: '12px',
      border: '2px solid #e5e7eb',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
        color: '#1f2937',
        fontSize: '1.3rem',
        fontWeight: '600',
      }}
    >
      Worked Example: Temperature Conversion (°C to °F)
    </h4>
    <p
      style={{ textAlign: 'center', marginBottom: '1.5rem', color: '#6b7280' }}
    >
      Formula: °F = (9/5) × °C + 32 &nbsp;&nbsp; (k = 1.8, c = 32)
    </p>

    <div
      style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}
    >
      <div
        style={{
          background: '#f0f9ff',
          padding: '1.25rem',
          borderRadius: '10px',
          border: '1px solid #0ea5e9',
        }}
      >
        <h5
          style={{ color: '#0284c7', marginBottom: '1rem', fontWeight: '600' }}
        >
          Celsius Data
        </h5>
        <p style={{ marginBottom: '0.75rem' }}>Values: 10, 15, 20, 25, 30 °C</p>
        <p>
          Mean: <strong>20°C</strong>
        </p>
        <p>
          SD: <strong>7.07°C</strong>
        </p>
        <p>
          Range: <strong>20°C</strong>
        </p>
      </div>

      <div
        style={{
          background: '#fef2f2',
          padding: '1.25rem',
          borderRadius: '10px',
          border: '1px solid #f87171',
        }}
      >
        <h5
          style={{ color: '#dc2626', marginBottom: '1rem', fontWeight: '600' }}
        >
          Fahrenheit Data
        </h5>
        <p style={{ marginBottom: '0.75rem' }}>Values: 50, 59, 68, 77, 86 °F</p>
        <p>
          Mean: 1.8 × 20 + 32 = <strong>68°F</strong>
        </p>
        <p>
          SD: 1.8 × 7.07 = <strong>12.73°F</strong>
        </p>
        <p>
          Range: 1.8 × 20 = <strong>36°F</strong>
        </p>
      </div>
    </div>

    <div
      style={{
        marginTop: '1.5rem',
        padding: '1rem',
        background: '#fefce8',
        borderRadius: '8px',
        border: '1px solid #eab308',
        textAlign: 'center',
      }}
    >
      <p style={{ fontWeight: '600', color: '#854d0e' }}>
        Key Insight: Adding 32 shifts all values but doesn't change spread (SD,
        Range).
        <br />
        Multiplying by 1.8 stretches the data, affecting both center AND spread.
      </p>
    </div>
  </div>
);

const StatisticsLesson = () => {
  const sections = [
    {
      id: 'central-tendency',
      title: 'Mean, Median, Mode',
      content: {
        introduction:
          'Measures of central tendency describe the center of a data set. Each measure provides different insights into the typical value.',

        concepts: [
          {
            title: 'Comparing Mean, Median, and Mode',
            explanation:
              'Mean is the average, median is the middle value, and mode is the most frequent value. Each is useful in different situations.',
            visual: <MeasuresComparisonVisual />,
            example: {
              problem:
                'Find the mean, median, and mode of: 8, 12, 8, 15, 10, 8, 20',
              solution: [
                'Order the data: 8, 8, 8, 10, 12, 15, 20',
                'Mean = (8+8+8+10+12+15+20)/7 = 81/7 ≈ 11.6',
                'Median = middle value (4th position) = 10',
                'Mode = most frequent value = 8 (appears 3 times)',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'finding-median',
      title: 'Finding Median',
      content: {
        introduction:
          "The median is the middle value when data is arranged in order. For even numbers of values, it's the average of the two middle values.",

        concepts: [
          {
            title: 'Median Calculation',
            explanation:
              'For odd n: median is the §(n+1)/2§th value. For even n: median is the average of the §(n/2)§th and §(n/2)+1§th values.',
            visual: <MedianFindingVisual />,
            example: {
              problem: 'Find the median of the set: 4, 1, 8, 5, 2, 9',
              solution: [
                'First, order the data: 1, 2, 4, 5, 8, 9',
                'n = 6 (even), so find average of 3rd and 4th values',
                '3rd value = 4, 4th value = 5',
                'Median = (4 + 5)/2 = 9/2 = 4.5',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'distribution-shapes',
      title: 'Distribution Shapes',
      content: {
        introduction:
          "The relationship between mean, median, and mode reveals the shape of a distribution - whether it's symmetric or skewed.",

        concepts: [
          {
            title: 'Skewness and Central Tendency',
            explanation:
              'In symmetric distributions, mean ≈ median ≈ mode. In right-skewed distributions, mean > median > mode. In left-skewed, mean < median < mode.',
            visual: <DistributionTypesVisual />,
            example: {
              problem:
                'If a distribution has mean = 75, median = 80, mode = 85, describe its shape',
              solution: [
                'Compare the three measures: mean < median < mode',
                'This pattern indicates a left-skewed distribution',
                'The mean is pulled down by lower values in the left tail',
                'Most values cluster around the mode (85) on the right side',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'outliers-effects',
      title: 'Outliers and Variability',
      content: {
        introduction:
          'Outliers significantly affect the mean but have minimal impact on the median. Understanding this helps choose appropriate measures.',

        concepts: [
          {
            title: 'Impact of Outliers',
            explanation:
              'The mean is sensitive to outliers because every value affects the calculation. The median is resistant to outliers because it depends only on position.',
            visual: <OutliersEffectVisual />,
            example: {
              problem:
                'House prices in a neighborhood: $200K, $210K, $205K, $215K, $2M. Which measure best represents typical price?',
              solution: [
                'Calculate both measures:',
                'Mean = §($200K + $210K + $205K + $215K + $2000K)/5§ = $566K',
                'Median = middle value = $210K',
                'The median ($210K) better represents the typical house price',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'quartiles-percentiles',
      title: 'Quartiles & Percentiles',
      content: {
        introduction:
          'Quartiles divide ordered data into four equal parts, while percentiles divide data into 100 parts. These measures help describe the spread and position of values within a dataset.',

        concepts: [
          {
            title: 'Understanding Quartiles',
            explanation:
              'Quartiles split data into quarters:\n\n• Q1 (25th percentile): 25% of data falls below this value\n• Q2 (50th percentile): The median - 50% falls below\n• Q3 (75th percentile): 75% of data falls below this value\n\nThe Interquartile Range (IQR) = Q3 - Q1 measures the spread of the middle 50% of data.',
            visual: <QuartileCalculationVisual />,
            example: {
              problem:
                'Find Q1, Q2, Q3, and IQR for the dataset: 12, 15, 18, 22, 25, 28, 32, 35, 40',
              solution: [
                'Order data (already ordered): 12, 15, 18, 22, 25, 28, 32, 35, 40',
                'n = 9, so Q2 (median) = 5th value = 25',
                'Lower half: 12, 15, 18, 22 → Q1 = §(15 + 18)/2§ = 16.5',
                'Upper half: 28, 32, 35, 40 → Q3 = §(32 + 35)/2§ = 33.5',
                'IQR = Q3 - Q1 = 33.5 - 16.5 = 17',
              ],
            },
          },
          {
            title: 'Percentiles in Context',
            explanation:
              'Percentiles are used extensively in standardized testing. If you score in the 85th percentile, you performed better than 85% of test-takers.\n\nKey percentiles to remember:\n• 25th percentile = Q1\n• 50th percentile = Q2 (Median)\n• 75th percentile = Q3\n• 90th, 95th, 99th often used for "top performers"',
            visual: <PercentileVisual />,
            example: {
              problem:
                'In a GRE test, John scored 320 which is the 80th percentile. What does this mean?',
              solution: [
                'The 80th percentile means John scored higher than 80% of test-takers',
                'Conversely, 20% of test-takers scored higher than John',
                'This does NOT mean John got 80% of questions correct',
                'Percentile is about relative position, not absolute score',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'box-plots',
      title: 'Box Plots & Data Analysis',
      content: {
        introduction:
          'Box plots (box-and-whisker plots) provide a visual summary of data distribution using the five-number summary: minimum, Q1, median, Q3, and maximum. They are powerful tools for comparing distributions and identifying outliers.',

        concepts: [
          {
            title: 'Reading Box Plots',
            explanation:
              'A box plot shows:\n\n• The "box" spans from Q1 to Q3 (containing the middle 50%)\n• A line inside the box marks the median\n• "Whiskers" extend to the minimum and maximum values\n• The length of the box represents the IQR\n• Outliers are often shown as individual points beyond the whiskers',
            visual: <BoxPlotVisual />,
            example: {
              problem:
                'A box plot shows: Min=20, Q1=35, Median=50, Q3=65, Max=90. What is the IQR and what percentage of data falls between 35 and 65?',
              solution: [
                'IQR = Q3 - Q1 = 65 - 35 = 30',
                'The box represents data between Q1 and Q3',
                'By definition, 50% of data falls within the box (between Q1 and Q3)',
                'So 50% of data falls between 35 and 65',
              ],
            },
          },
          {
            title: 'Detecting Outliers with IQR',
            explanation:
              'The 1.5×IQR rule identifies outliers:\n\n• Lower fence = Q1 - 1.5 × IQR\n• Upper fence = Q3 + 1.5 × IQR\n\nAny value below the lower fence or above the upper fence is considered an outlier. This is a robust method because IQR itself is resistant to outliers.',
            visual: <IQROutlierVisual />,
            example: {
              problem:
                'For a dataset with Q1=40, Q3=70, determine if 5 and 120 are outliers.',
              solution: [
                'IQR = Q3 - Q1 = 70 - 40 = 30',
                'Lower fence = Q1 - 1.5 × IQR = 40 - 45 = -5',
                'Upper fence = Q3 + 1.5 × IQR = 70 + 45 = 115',
                '5 > -5, so 5 is NOT an outlier',
                '120 > 115, so 120 IS an outlier',
              ],
            },
          },
          {
            title: 'Comparing Distributions',
            explanation:
              'Box plots are excellent for comparing multiple datasets:\n\n• Compare medians to see which group has higher center\n• Compare box lengths (IQR) to see which has more variability\n• Compare positions to see overlap between groups\n• Check for skewness by median position within the box',
            visual: <ComparingBoxPlotsVisual />,
            example: {
              problem:
                'Two classes took an exam. Class A: Median=75, IQR=10. Class B: Median=72, IQR=25. Which class performed more consistently?',
              solution: [
                'Class A has a higher median (75 vs 72), indicating better average performance',
                'Class A has a smaller IQR (10 vs 25)',
                'Smaller IQR means scores are more clustered around the median',
                'Therefore, Class A performed more consistently',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'transformations',
      title: 'Effects of Transformations on Statistics',
      content: {
        introduction:
          'Understanding how adding or multiplying by a constant affects statistical measures is crucial for GRE problems. This knowledge helps you quickly determine new values without recalculating from scratch.',

        concepts: [
          {
            title: 'Adding a Constant to All Values',
            explanation:
              'When you add a constant k to every value in a dataset:\n\n• Mean increases by k (new mean = old mean + k)\n• Median increases by k\n• Mode increases by k (if one exists)\n• Range stays the SAME (differences unchanged)\n• Standard deviation stays the SAME\n• Variance stays the SAME\n• IQR stays the SAME\n\nThink of it as shifting the entire distribution left or right without changing its shape or spread.',
            visual: <TransformationEffectsVisual />,
            example: {
              problem:
                'A dataset has mean = 50, median = 48, and standard deviation = 10. If 7 is added to each value, what are the new mean, median, and standard deviation?',
              solution: [
                'Adding a constant shifts center measures but not spread measures',
                'New Mean = 50 + 7 = 57',
                'New Median = 48 + 7 = 55',
                'New SD = 10 (unchanged - spread is not affected by addition)',
              ],
            },
          },
          {
            title: 'Multiplying All Values by a Constant',
            explanation:
              'When you multiply every value by a constant §k§:\n\n• Mean is multiplied by §k§ (new mean = §k§ × old mean)\n• Median is multiplied by §k§\n• Mode is multiplied by §k§\n• Range is multiplied by §|k|§ (absolute value)\n• Standard deviation is multiplied by §|k|§\n• Variance is multiplied by §k²§ (k squared)\n• IQR is multiplied by §|k|§\n\nThis stretches or compresses the distribution, affecting both center AND spread.',
            example: {
              problem:
                'Test scores have mean = 75 and SD = 8. The teacher decides to multiply all scores by 1.2 (20% bonus). Find the new mean and SD.',
              solution: [
                'Multiplying affects both center and spread',
                'New Mean = §1.2§ × §75§ = §90§',
                'New SD = §|1.2|§ × §8§ = §9.6§',
                'Note: Variance would be §(1.2)²§ × §64§ = §1.44§ × §64§ = §92.16§',
              ],
            },
          },
          {
            title: 'Combined Transformations (Y = kX + c)',
            explanation:
              'Real-world problems often combine multiplication and addition (linear transformations):\n\n• For §Y = kX + c§:\n• New Mean = §k§ × (Old Mean) + c\n• New Median = §k§ × (Old Median) + c\n• New SD = §|k|§ × (Old SD) — the constant c does NOT affect SD\n• New Variance = §k²§ × (Old Variance)\n• New Range = §|k|§ × (Old Range)\n\nKey insight: The additive constant only affects position, while the multiplicative constant affects both position and spread.',
            visual: <TransformationExampleVisual />,
            example: {
              problem:
                'Celsius temperatures have mean = §20°C§ and SD = §5°C§. Convert to Fahrenheit using F = §(9/5)C§ + §32§. Find the new mean and SD.',
              solution: [
                'This is §Y = kX + c§ where §k§ = §9/5§ = §1.8§ and §c§ = §32§',
                'New Mean = §1.8§ × §20§ + §32§ = §36§ + §32§ = §68°F§',
                'New SD = §|1.8|§ × §5§ = §9°F§',
                'Note: The §+32§ does NOT affect the SD, only the §×1.8§ does',
              ],
            },
          },
          {
            title: 'Quick Reference: What Changes?',
            explanation:
              'Summary table for transformations:\n\nAdding §k§:\n• Changes: Mean, Median, Mode, Min, Max, Quartiles\n• Unchanged: Range, SD, Variance, IQR\n\nMultiplying by §k§:\n• Changes: Everything!\n• Mean, Median, Mode → multiply by §k§\n• Range, SD, IQR → multiply by §|k|§\n• Variance → multiply by §k²§\n\nWhy this matters on GRE:\nMany problems ask "if all values increase by 5..." or "if all values are doubled..." - knowing these rules lets you answer instantly!',
            example: {
              problem:
                'A dataset has: Mean = §40§, Median = §38§, SD = §6§, Range = §20§. Each value is tripled then 10 is added. Find all new statistics.',
              solution: [
                'First triple (×3): Mean = §120§, Median = §114§, SD = §18§, Range = §60§',
                'Then add 10: Mean = §130§, Median = §124§, SD = §18§, Range = §60§',
                'New Mean = §3§ × §40§ + §10§ = §130§',
                'New Median = §3§ × §38§ + §10§ = §124§',
                "New SD = §3§ × §6§ = §18§ (addition doesn't change SD)",
                "New Range = §3§ × §20§ = §60§ (addition doesn't change range)",
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'timed',
      timeLimit: 90,
      question: 'What is the median of: 3, 8, 5, 12, 9, 7, 15?',
      options: ['8', '7', '9', '5', '12'],
      correct: 0,
      explanation:
        'Order the data: 3, 5, 7, 8, 9, 12, 15. The middle (4th) value is 8.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'The numbers of students in 6 classes are 25, 28, 29, 30, 31, 35. What is the median number of students?',
      options: ['29.5', '29', '30', '30.5', '31'],
      correct: 0,
      explanation:
        'Data is already ordered. With an even number of values (6), the median is the average of the two middle values (29 and 30). §(29+30)/2 = 29.5§',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'A data set is: 5, 10, 15, 5, 20, 5, 25. What is the mode?',
      options: ['5', '10', '15', '20', 'No mode'],
      correct: 0,
      explanation:
        'The mode is the most frequently occurring value in a data set. The value 5 appears 3 times, which is more than any other value.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'The mean of five numbers is 12. If four of the numbers are 8, 10, 14, and 16, what is the fifth number?',
      options: ['12', '8', '10', '14', '6'],
      correct: 0,
      explanation:
        'Mean = 12, so sum = 12 × 5 = 60. Sum of four numbers = 8 + 10 + 14 + 16 = 48. Fifth number = 60 - 48 = 12.',
    },
    {
      type: 'timed',
      timeLimit: 95,
      question:
        'A dataset has values: 2, 4, 6, 8, 10. What is the mean absolute deviation from the mean?',
      options: ['2.4', '3', '2', '1.6', '4'],
      correct: 0,
      explanation:
        'Mean = 6. Deviations: |2-6|=4, |4-6|=2, |6-6|=0, |8-6|=2, |10-6|=4. Mean absolute deviation = §(4+2+0+2+4)/5§ = §12/5§ = §2.4§',
    },
    {
      type: 'timed',
      timeLimit: 125,
      question:
        'A data set has values: {12, 15, 18, 18, 20, 22, 25}. What is the mode?',
      options: ['18', '15', '20', '12', 'No mode'],
      correct: 0,
      explanation:
        'The mode is the value that appears most frequently. §18§ appears twice, all others once. Mode = 18.',
    },
    {
      type: 'timed',
      timeLimit: 135,
      question:
        'If the mean of 5 numbers is 40, and four of them are 30, 35, 42, and 48, what is the fifth number?',
      options: ['45', '50', '40', '38', '52'],
      correct: 0,
      explanation:
        'Sum of 5 numbers = §5 × 40 = 200§. Sum of 4 known = §30 + 35 + 42 + 48 = 155§. Fifth = §200 - 155 = 45§.',
    },
    {
      type: 'timed',
      question:
        'Which measure of central tendency is most affected by outliers?',
      options: ['Mean', 'Median', 'Mode', 'Range', 'All are equally affected'],
      correct: 0,
      explanation:
        'The mean is most affected by outliers because its calculation includes every value in the data set.',
    },
    {
      type: 'timed',
      question: 'A dataset has an odd number of values. The median is always:',
      options: [
        'One of the values in the set',
        'The average of two values',
        'The smallest value',
        'The largest value',
        'Equal to the mean',
      ],
      correct: 0,
      explanation:
        'When there is an odd number of sorted values, the median is always the middle value, which is one of the actual values in the set.',
    },
    {
      type: 'timed',
      question:
        'In a right-skewed distribution, which relationship is typically true?',
      options: [
        'Mean > Median > Mode',
        'Mode > Median > Mean',
        'Mean = Median = Mode',
        'Median > Mean > Mode',
        'Cannot determine',
      ],
      correct: 0,
      explanation:
        'In a right-skewed distribution, the tail extends to the right, pulling the mean higher than the median, which is higher than the mode.',
    },
    {
      type: 'timed',
      question:
        'The range of a dataset is 20 and the smallest value is 15. What is the largest value?',
      options: ['35', '5', '20', '30', '25'],
      correct: 0,
      explanation:
        'Range = Maximum - Minimum. So 20 = Maximum - 15, which means Maximum = 35.',
    },
    {
      type: 'timed',
      question:
        'Which measure of central tendency is best for highly skewed data?',
      options: ['Median', 'Mean', 'Mode', 'Range', 'Standard deviation'],
      correct: 0,
      explanation:
        'The median is resistant to outliers and extreme values, making it the best measure of central tendency for highly skewed data.',
    },
    {
      type: 'timed',
      question: 'A dataset has values: {5, 10, 10, 15, 20}. What is the range?',
      options: ['15', '10', '20', '5', '12'],
      correct: 0,
      explanation: 'Range = Maximum - Minimum = 20 - 5 = 15.',
    },
    {
      type: 'timed',
      question:
        'If every value in a dataset is multiplied by 3, what happens to the mean?',
      options: [
        'Multiplied by 3',
        'Stays the same',
        'Divided by 3',
        'Squared',
        'Increased by 3',
      ],
      correct: 0,
      explanation:
        'When all values are multiplied by a constant, the mean is also multiplied by that constant. Mean becomes 3 times larger.',
    },
    {
      type: 'timed',
      question:
        'A class has 10 students with an average score of 75. If 2 new students with scores 80 and 82 join, what is the new average?',
      options: ['76', '77', '75.5', '78', '76.5'],
      correct: 0,
      explanation:
        'Total of original 10 = 10 × 75 = 750. New total = 750 + 80 + 82 = 912. New average = §912/12§ = 76.',
    },

    {
      type: 'untimed',
      timeLimit: 90,
      question: 'What is the median of: 3, 8, 5, 12, 9, 7, 15?',
      options: ['8', '7', '9', '5', '12'],
      correct: 0,
      explanation:
        'Order the data: 3, 5, 7, 8, 9, 12, 15. The middle (4th) value is 8.',
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question:
        'The numbers of students in 6 classes are 25, 28, 29, 30, 31, 35. What is the median number of students?',
      options: ['29.5', '29', '30', '30.5', '31'],
      correct: 0,
      explanation:
        'Data is already ordered. With an even number of values (6), the median is the average of the two middle values (29 and 30). §(29+30)/2 = 29.5§',
    },
    {
      type: 'untimed',
      timeLimit: 100,
      question: 'A data set is: 5, 10, 15, 5, 20, 5, 25. What is the mode?',
      options: ['5', '10', '15', '20', 'No mode'],
      correct: 0,
      explanation:
        'The mode is the most frequently occurring value in a data set. The value 5 appears 3 times, which is more than any other value.',
    },
    {
      type: 'untimed',
      timeLimit: 110,
      question:
        'The mean of five numbers is 12. If four of the numbers are 8, 10, 14, and 16, what is the fifth number?',
      options: ['12', '8', '10', '14', '6'],
      correct: 0,
      explanation:
        'Mean = 12, so sum = 12 × 5 = 60. Sum of four numbers = 8 + 10 + 14 + 16 = 48. Fifth number = 60 - 48 = 12.',
    },
    {
      type: 'untimed',
      timeLimit: 95,
      question:
        'A dataset has values: 2, 4, 6, 8, 10. What is the mean absolute deviation from the mean?',
      options: ['2.4', '3', '2', '1.6', '4'],
      correct: 0,
      explanation:
        'Mean = 6. Deviations: |2-6|=4, |4-6|=2, |6-6|=0, |8-6|=2, |10-6|=4. Mean absolute deviation = §(4+2+0+2+4)/5§ = §12/5§ = §2.4§',
    },
    {
      type: 'untimed',
      timeLimit: 125,
      question:
        'A data set has values: {12, 15, 18, 18, 20, 22, 25}. What is the mode?',
      options: ['18', '15', '20', '12', 'No mode'],
      correct: 0,
      explanation:
        'The mode is the value that appears most frequently. §18§ appears twice, all others once. Mode = 18.',
    },
    {
      type: 'untimed',
      timeLimit: 135,
      question:
        'If the mean of 5 numbers is 40, and four of them are 30, 35, 42, and 48, what is the fifth number?',
      options: ['45', '50', '40', '38', '52'],
      correct: 0,
      explanation:
        'Sum of 5 numbers = §5 × 40 = 200§. Sum of 4 known = §30 + 35 + 42 + 48 = 155§. Fifth = §200 - 155 = 45§.',
    },
    {
      type: 'untimed',
      question:
        'Which measure of central tendency is most affected by outliers?',
      options: ['Mean', 'Median', 'Mode', 'Range', 'All are equally affected'],
      correct: 0,
      explanation:
        'The mean is most affected by outliers because its calculation includes every value in the data set.',
    },
    {
      type: 'untimed',
      question: 'A dataset has an odd number of values. The median is always:',
      options: [
        'One of the values in the set',
        'The average of two values',
        'The smallest value',
        'The largest value',
        'Equal to the mean',
      ],
      correct: 0,
      explanation:
        'When there is an odd number of sorted values, the median is always the middle value, which is one of the actual values in the set.',
    },
    {
      type: 'untimed',
      question:
        'In a right-skewed distribution, which relationship is typically true?',
      options: [
        'Mean > Median > Mode',
        'Mode > Median > Mean',
        'Mean = Median = Mode',
        'Median > Mean > Mode',
        'Cannot determine',
      ],
      correct: 0,
      explanation:
        'In a right-skewed distribution, the tail extends to the right, pulling the mean higher than the median, which is higher than the mode.',
    },
    {
      type: 'untimed',
      question:
        'The range of a dataset is 20 and the smallest value is 15. What is the largest value?',
      options: ['35', '5', '20', '30', '25'],
      correct: 0,
      explanation:
        'Range = Maximum - Minimum. So 20 = Maximum - 15, which means Maximum = 35.',
    },
    {
      type: 'untimed',
      question:
        'Which measure of central tendency is best for highly skewed data?',
      options: ['Median', 'Mean', 'Mode', 'Range', 'Standard deviation'],
      correct: 0,
      explanation:
        'The median is resistant to outliers and extreme values, making it the best measure of central tendency for highly skewed data.',
    },
    {
      type: 'untimed',
      question: 'A dataset has values: {5, 10, 10, 15, 20}. What is the range?',
      options: ['15', '10', '20', '5', '12'],
      correct: 0,
      explanation: 'Range = Maximum - Minimum = 20 - 5 = 15.',
    },
    {
      type: 'untimed',
      question:
        'If every value in a dataset is multiplied by 3, what happens to the mean?',
      options: [
        'Multiplied by 3',
        'Stays the same',
        'Divided by 3',
        'Squared',
        'Increased by 3',
      ],
      correct: 0,
      explanation:
        'When all values are multiplied by a constant, the mean is also multiplied by that constant. Mean becomes 3 times larger.',
    },
    {
      type: 'untimed',
      question:
        'A class has 10 students with an average score of 75. If 2 new students with scores 80 and 82 join, what is the new average?',
      options: ['76', '77', '75.5', '78', '76.5'],
      correct: 0,
      explanation:
        'Total of original 10 = 10 × 75 = 750. New total = 750 + 80 + 82 = 912. New average = §912/12§ = 76.',
    },
    // Quartile and Percentile Questions - Timed
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'For the dataset 4, 8, 12, 16, 20, 24, 28, what is the interquartile range (IQR)?',
      options: ['12', '16', '8', '20', '24'],
      correct: 1,
      explanation:
        'Median (Q2) = 16 (middle value). Lower half: 4, 8, 12 → Q1 = 8. Upper half: 20, 24, 28 → Q3 = 24. IQR = Q3 - Q1 = 24 - 8 = 16.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question:
        'In a box plot, the median is located at 50. If Q1 = 35 and Q3 = 70, what percentage of data falls between Q1 and the median?',
      options: ['25%', '50%', '75%', '35%', '15%'],
      correct: 0,
      explanation:
        'By definition, Q1 represents the 25th percentile and the median represents the 50th percentile. The data between Q1 and median is 50% - 25% = 25% of all data.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'A dataset has Q1 = 20 and Q3 = 50. Using the 1.5×IQR rule, what is the lower fence for outlier detection?',
      options: ['-25', '5', '-5', '0', '-15'],
      correct: 0,
      explanation:
        'IQR = Q3 - Q1 = 50 - 20 = 30. Lower fence = Q1 - 1.5 × IQR = 20 - 1.5 × 30 = 20 - 45 = -25.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'If a student scores in the 90th percentile on a test, which statement is TRUE?',
      options: [
        'The student scored higher than 90% of test-takers',
        'The student got 90% of questions correct',
        'The student scored in the top 90% of test-takers',
        '90% of students scored higher than this student',
        'The student answered 90 questions correctly',
      ],
      correct: 0,
      explanation:
        'The 90th percentile means the student performed better than 90% of all test-takers. It is a measure of relative standing, not absolute performance.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'For a dataset, Q1 = 30, Median = 45, Q3 = 55. Which statement about the distribution is most likely true?',
      options: [
        'The distribution is right-skewed',
        'The distribution is left-skewed',
        'The distribution is symmetric',
        'The distribution is bimodal',
        'Cannot be determined',
      ],
      correct: 1,
      explanation:
        'The median (45) is closer to Q3 (55) than to Q1 (30). Distance from Q1 to median = 15, distance from median to Q3 = 10. When the median is closer to Q3, the distribution is left-skewed (longer tail on the left).',
    },
    {
      type: 'timed',
      timeLimit: 130,
      question:
        'Two box plots are shown for Class A and Class B. Class A has IQR = 15 with median = 70. Class B has IQR = 8 with median = 68. Which class shows more consistent performance?',
      options: [
        'Class B, because it has smaller IQR',
        'Class A, because it has higher median',
        'Class A, because it has larger IQR',
        'Class B, because it has lower median',
        'Both are equally consistent',
      ],
      correct: 0,
      explanation:
        'Consistency is measured by spread, not center. A smaller IQR means the middle 50% of scores are more tightly clustered. Class B has IQR = 8, which is smaller than Class A IQR = 15, so Class B is more consistent.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question:
        'The five-number summary for a dataset is: 10, 25, 40, 55, 80. What is the range?',
      options: ['70', '30', '45', '40', '55'],
      correct: 0,
      explanation:
        'The five-number summary is: Min, Q1, Median, Q3, Max. So Min = 10 and Max = 80. Range = Max - Min = 80 - 10 = 70.',
    },
    {
      type: 'timed',
      timeLimit: 115,
      question:
        'In a box plot, if the whisker on the right is much longer than the whisker on the left, what does this indicate?',
      options: [
        'The distribution is right-skewed',
        'The distribution is left-skewed',
        'The distribution is symmetric',
        'There are more outliers on the left',
        'The median equals the mean',
      ],
      correct: 0,
      explanation:
        'A longer right whisker indicates that the data extends further on the right side, meaning there are values far above the median. This is characteristic of a right-skewed (positively skewed) distribution.',
    },
    // Quartile and Percentile Questions - Untimed
    {
      type: 'untimed',
      question:
        'For the dataset 4, 8, 12, 16, 20, 24, 28, what is the value of Q3?',
      options: ['24', '20', '22', '26', '28'],
      correct: 0,
      explanation:
        'With 7 values, the median is the 4th value (16). Q3 is the median of the upper half (20, 24, 28), which is 24.',
    },
    {
      type: 'untimed',
      question:
        'A dataset has Q1 = 40 and Q3 = 80. What value would be considered an outlier using the 1.5×IQR rule?',
      options: ['150', '100', '30', '85', '35'],
      correct: 0,
      explanation:
        'IQR = 80 - 40 = 40. Upper fence = Q3 + 1.5 × IQR = 80 + 60 = 140. Lower fence = Q1 - 1.5 × IQR = 40 - 60 = -20. 150 > 140, so it is an outlier. The other values fall within the fences.',
    },
    {
      type: 'untimed',
      question:
        'If the 75th percentile of test scores is 85, what does this tell us?',
      options: [
        '75% of test-takers scored 85 or below',
        '75% of test-takers scored 85 or above',
        '85% of test-takers scored 75 or below',
        'The average score is 85',
        '75 students scored 85',
      ],
      correct: 0,
      explanation:
        'The Pth percentile is the value below which P% of data falls. So the 75th percentile of 85 means 75% of test-takers scored 85 or below.',
    },
    {
      type: 'untimed',
      question: 'In a box plot, what does the length of the box represent?',
      options: [
        'The interquartile range (IQR)',
        'The range of all data',
        'The standard deviation',
        'The mean',
        'The variance',
      ],
      correct: 0,
      explanation:
        'The box in a box plot extends from Q1 to Q3. The length of the box equals Q3 - Q1, which is the interquartile range (IQR). It represents the spread of the middle 50% of data.',
    },
    {
      type: 'untimed',
      question:
        'For a symmetric distribution, which relationship between Q1, median, and Q3 is expected?',
      options: [
        'Median - Q1 = Q3 - Median',
        'Q1 + Q3 = 2 × Mean',
        'Q3 - Q1 = Range',
        'Q1 = Median - Q3',
        'Q1 × Q3 = Median²',
      ],
      correct: 0,
      explanation:
        'In a symmetric distribution, the median is equidistant from Q1 and Q3. This means (Median - Q1) = (Q3 - Median). The median lies exactly in the middle of the box in a box plot.',
    },
    {
      type: 'untimed',
      question:
        'A box plot shows a median at 60, with Q1 at 45 and Q3 at 65. What can we conclude about the distribution?',
      options: [
        'It is left-skewed because the lower half is more spread out',
        'It is right-skewed because the upper half is more spread out',
        'It is symmetric',
        'It is bimodal',
        'We need more information',
      ],
      correct: 0,
      explanation:
        'Distance from Q1 to median = 60 - 45 = 15. Distance from median to Q3 = 65 - 60 = 5. The lower half is more spread out (longer left side of box), indicating a left-skewed distribution.',
    },
    {
      type: 'untimed',
      question:
        'If a value falls at the 60th percentile, approximately what fraction of data lies above it?',
      options: ['2/5', '3/5', '1/2', '1/3', '1/4'],
      correct: 0,
      explanation:
        'The 60th percentile means 60% of data is below this value. Therefore, 100% - 60% = 40% = 2/5 of the data lies above it.',
    },
    {
      type: 'untimed',
      question:
        'Dataset A has IQR = 20 and Dataset B has IQR = 35. What can we conclude?',
      options: [
        'Dataset A has less variability in its middle 50%',
        'Dataset A has a higher median',
        'Dataset B has fewer outliers',
        'Dataset A has a smaller range',
        'Both datasets have the same median',
      ],
      correct: 0,
      explanation:
        'IQR measures the spread of the middle 50% of data. A smaller IQR (20 vs 35) indicates that the middle 50% of values in Dataset A are more tightly clustered, meaning less variability.',
    },
    // Transformation Questions - Timed
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'A dataset has mean = 30 and SD = 5. If 10 is added to every value, what are the new mean and SD?',
      options: [
        'Mean = 40, SD = 5',
        'Mean = 40, SD = 15',
        'Mean = 30, SD = 15',
        'Mean = 40, SD = 50',
        'Mean = 300, SD = 50',
      ],
      correct: 0,
      explanation:
        'Adding a constant to all values shifts the mean by that constant but does NOT change the standard deviation. New mean = 30 + 10 = 40. SD remains 5.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question:
        'A dataset has mean = 20, median = 18, and range = 15. If every value is doubled, what is the new range?',
      options: ['30', '15', '45', '60', '17'],
      correct: 0,
      explanation:
        'When all values are multiplied by a constant k, the range is also multiplied by |k|. New range = 2 × 15 = 30.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'If every value in a dataset is multiplied by 2, what happens to the standard deviation?',
      options: [
        'It doubles',
        'It stays the same',
        'It quadruples',
        'It halves',
        'It increases by 2',
      ],
      correct: 0,
      explanation:
        'When all values are multiplied by k, the standard deviation is multiplied by |k|. So SD doubles when values are doubled.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'A dataset has variance = 16. If every value is multiplied by 3, what is the new variance?',
      options: ['144', '48', '16', '64', '12'],
      correct: 0,
      explanation:
        'Variance is multiplied by k² when all values are multiplied by k. New variance = 3² × 16 = 9 × 16 = 144.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question:
        'Temperatures in Celsius have mean = 25°C and SD = 4°C. Using F = (9/5)C + 32, what is the SD in Fahrenheit?',
      options: ['7.2°F', '77°F', '4°F', '39.2°F', '36°F'],
      correct: 0,
      explanation:
        'For Y = kX + c, new SD = |k| × old SD. Here k = 9/5 = 1.8. New SD = 1.8 × 4 = 7.2°F. The +32 does NOT affect SD.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'A dataset has mean = 50, range = 20. Each value is tripled then 5 is added. What is the new range?',
      options: ['60', '65', '20', '25', '55'],
      correct: 0,
      explanation:
        'Range is affected by multiplication but not by addition. New range = 3 × 20 = 60. Adding 5 does not change the range.',
    },
    // Transformation Questions - Untimed
    {
      type: 'untimed',
      question:
        'If 5 is subtracted from every value in a dataset, what happens to the standard deviation?',
      options: [
        'It stays the same',
        'It decreases by 5',
        'It increases by 5',
        'It becomes negative',
        'It is divided by 5',
      ],
      correct: 0,
      explanation:
        'Adding or subtracting a constant shifts all values but does not change the spread. The standard deviation remains unchanged.',
    },
    {
      type: 'untimed',
      question:
        'Test scores have mean = 70 and SD = 12. A teacher curves by adding 15 points to each score. What is the new mean and SD?',
      options: [
        'Mean = 85, SD = 12',
        'Mean = 85, SD = 27',
        'Mean = 70, SD = 27',
        'Mean = 85, SD = 15',
        'Mean = 1050, SD = 180',
      ],
      correct: 0,
      explanation:
        'Adding a constant increases the mean by that constant but leaves SD unchanged. New mean = 70 + 15 = 85. SD = 12.',
    },
    {
      type: 'untimed',
      question:
        'Dataset has Q1 = 20 and Q3 = 50. If all values are multiplied by 2, what is the new IQR?',
      options: ['60', '30', '70', '100', '40'],
      correct: 0,
      explanation:
        'Original IQR = 50 - 20 = 30. When multiplied by 2, new Q1 = 40, new Q3 = 100. New IQR = 100 - 40 = 60. Or simply: new IQR = 2 × 30 = 60.',
    },
    {
      type: 'untimed',
      question:
        'Which of these statistics is NOT affected by adding a constant to all values?',
      options: [
        'Standard deviation',
        'Mean',
        'Median',
        'Mode',
        'Minimum value',
      ],
      correct: 0,
      explanation:
        'Adding a constant shifts all measures of center (mean, median, mode) and position (min, max), but does NOT change measures of spread like standard deviation, variance, range, or IQR.',
    },
    {
      type: 'untimed',
      question:
        'A dataset has mean = 100. Each value is first multiplied by 0.5, then 20 is added. What is the new mean?',
      options: ['70', '120', '60', '170', '50'],
      correct: 0,
      explanation:
        'For transformation Y = kX + c: New mean = k × (old mean) + c = 0.5 × 100 + 20 = 50 + 20 = 70.',
    },
    {
      type: 'untimed',
      question:
        'A dataset has SD = 8. If each value is divided by 4, what is the new SD?',
      options: ['2', '8', '4', '32', '0.5'],
      correct: 0,
      explanation:
        'Dividing by 4 is the same as multiplying by 0.25 (or 1/4). New SD = |0.25| × 8 = 2. The SD is divided by 4.',
    },
    {
      type: 'untimed',
      question:
        'Heights in inches have mean = 66 and SD = 3. Converted to centimeters (multiply by 2.54), what is the new SD?',
      options: ['7.62 cm', '3 cm', '167.64 cm', '6 cm', '1.18 cm'],
      correct: 0,
      explanation:
        'When multiplying by a constant, SD is also multiplied by that constant. New SD = 2.54 × 3 = 7.62 cm.',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question:
        'For the data set §2, 4, 6, 8, 10§, compare the two quantities.',
      quantityA: 'Mean',
      quantityB: 'Median',
      correct: 2,
      explanation:
        'Compare the two quantities directly. The two quantities are equal.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question:
        'Which measures are affected by an extreme outlier? Select all that apply.',
      options: ['Mean', 'Median', 'Range', 'Standard deviation', 'Mode only'],
      correct: [0, 2, 3],
      explanation:
        'The mean, range, and standard deviation usually change strongly when an extreme outlier is added.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      questionType: 'numeric_entry',
      question: 'What is the median of §3, 7, 8, 10, 12§?',
      correctAnswer: '8',
      acceptedAnswers: ['8'],
      explanation: 'The data are already ordered, and the middle value is 8.',
    },
  ];

  const quickReference = [
    '§Mean = sum ÷ n§ (average)',
    '§Median§ = middle value when ordered',
    '§Mode§ = most frequent value',
    '§Q1§ = 25th percentile, §Q3§ = 75th percentile, §Q2§ = §Median§',
    '§IQR = Q3 - Q1§ (interquartile range)',
    '§Outlier§ if §value§ < §Q1 - 1.5×IQR§ or §value§ > §Q3 + 1.5×IQR§',
    '§Mean§ is sensitive to outliers',
    '§Median§ and §IQR§ are resistant to outliers',
    '§Range = max - min§',
    'Box plot shows: Min, Q1, Median, Q3, Max',
    'Adding §k§: §Mean§ and §Median§ §↑§ by §k§; But §SD§ and§Range§ unchanged',
    'Multiplying by §k§: §Mean§, §Median§ × §k§; §SD§ × §|k|§; §Variance§ × §k²§',
    '§Y = kX + c§: new §mean§ = §k§(old §mean§) + §c§; new §SD§ = §|k|§(old §SD§)',
  ];

  const getStartedWithProps = {
    title: 'Need help with Statistics concepts?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I need help understanding Statistics concepts for the GRE Quantitative section',
    aicontext:
      'Master mean, median, mode, quartiles, percentiles, box plots, IQR, transformations, and data interpretation',
  };

  return (
    <LessonTemplate
      lessonId="statistics"
      lessonTitle="Statistics: Central Tendency, Transformations & Box Plots"
      lessonIcon={<FiBarChart2 />}
      lessonDescription="Master mean, median, mode, quartiles, transformations, and box plot analysis"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={
        <QuantLessonPracticeSlot lessonId="statistics" />
      }
    />
  );
};

export default StatisticsLesson;
